import {
  createVideoPlayer,
  PlayerEvent,
  LogLevel,
  removeVideoPlayer,
  Autoplay,
  PerformanceMode,
} from '@cbsinteractive/avia-js';
import { hls } from '@cbsinteractive/avia-js-hls';
import { dash } from '@cbsinteractive/avia-js-dash';
import { gam } from '@cbsinteractive/avia-js-gam';

import config from 'config';

type PlayerEventCallback = (event: unknown) => void;
type AviaPlayer = ReturnType<typeof createVideoPlayer> extends Promise<infer T> ? T : never;
type ResourceConfig = Parameters<AviaPlayer['attachResource']>[0];

export default class Player {
  aviaPlayer!: AviaPlayer;

  resource!: ResourceConfig;

  onPlayerEvent: PlayerEventCallback;

  constructor(onPlayerEvent: PlayerEventCallback) {
    this.onPlayerEvent = onPlayerEvent;
  }

  async initializePlayer(container: HTMLElement, id: string): Promise<void> {
    const plugins = [hls(), dash(), gam({ debug: config.playback.debug })];
    try {
      let analyticsConsent;
      window.cmp.addEventListener(
        window.ParamountCmp.PrivacyEvent.CONSENT_READY,
        () => {
          // query analytics category
          analyticsConsent = window.cmp.consent.getConsent(
            window.ParamountCmp.ConsentCategory.ANALYTICS,
          );
        },
      );

      const newVTG = { ...window.vtg.config };
      if (analyticsConsent === false) {
        // set flags for tracking libraries to analytics category consent
        newVTG.AdobeData.enabled = false;
        newVTG.AdobeMedia.enabled = false;
        newVTG.ComscoreStreaming.enabled = false;
        newVTG.Mux.enabled = false;
        newVTG.Nielsen.enabled = false;
      }

      if (window.vtg.config) {
        const trackingPlugin = window.vtg.ts.AviaTracking.plugin({
          config: newVTG,
        }) as typeof plugins[number];
        plugins.push(trackingPlugin);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Avia tracking plugin error:', e.message);
    }
    // 1. Create the video player
    const player = await createVideoPlayer({
      container,
      autoplay: Autoplay.ATTEMPT_MUTED,
      id,
      performanceMode: PerformanceMode.TV,
      renderTextTrackNatively: false,
      plugins,
      network: { retryAttempts: 3, retryIntervals: [1000] },
      logLevel: config.playback.debug ? LogLevel.DEBUG : LogLevel.OFF,
      useNativeFullscreen: false,
      // Throwing this in for future use if we ever need to enable
      // extremely low-level logging
      // overrides: {
      //   enableLowLevelStreamingLogs: config.playback.debug,
      // },
    });
    this.aviaPlayer = player;

    // 2. Attach event listeners
    Object.keys(PlayerEvent).forEach(event => {
      player.on(PlayerEvent[event], this.onPlayerEvent);
    });

    // 3. Set the resource separately when the player and video are both ready
  }

  play(): void {
    this.aviaPlayer.play();
  }

  pause(): void {
    this.aviaPlayer.pause();
  }

  seek(pos: number): void {
    this.aviaPlayer.seek(pos);
  }

  enableTextTrack(enable: boolean): void {
    if (this.aviaPlayer.textTrackEnabled !== enable) {
      this.aviaPlayer.textTrackEnabled = enable;
    } else {
      this.aviaPlayer.textTrackEnabled = !enable;
      this.aviaPlayer.textTrackEnabled = enable;
    }
  }

  resetResource(): void {
    this.aviaPlayer.attachResource(this.resource).catch(e => {
      // eslint-disable-next-line no-console
      console.error(
        'A problem occurred resetting the resource of the Avia player ',
        e,
      );
    });
  }

  setResource(resource: ResourceConfig): void {
    this.resource = resource;
    this.aviaPlayer.attachResource(resource).catch(e => {
      // eslint-disable-next-line no-console
      console.error(
        'A problem occurred attaching the resource to the Avia player ',
        e,
      );
    });
  }

  destroy(): void {
    if (this.aviaPlayer) {
      Object.keys(PlayerEvent).forEach(event => {
        this.aviaPlayer.off(PlayerEvent[event], this.onPlayerEvent);
      });
      removeVideoPlayer(this.aviaPlayer.id);
    }
  }

  mute(bool: boolean): void {
    this.aviaPlayer.muted = bool;
  }

  enterFullscreen(): void {
    this.aviaPlayer.enterFullscreen();
  }

  exitFullscreen(): void {
    this.aviaPlayer.exitFullscreen();
  }

  static PlayerEvent = PlayerEvent;
}
